import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Barbell, ForkKnife, ChartBar, ChatCircleDots, Books, Pulse, ArrowRight, LockKey, Sparkle } from "@phosphor-icons/react";
import { canAccess, REQUIRES, PLAN_NAMES, planNameForLevel } from "@/lib/plans";
import WelcomeModal from "@/components/WelcomeModal";
import WelcomeBanner from "@/components/WelcomeBanner";

const CLIENT_MODULES = [
  { to: "/nutricion", tKey: "nutrition", icon: ForkKnife, required: REQUIRES.NUTRITION_TEMPLATES },
  { to: "/entrenamiento", tKey: "training", icon: Barbell, required: REQUIRES.TRAINING },
  { to: "/seguimiento", tKey: "tracking", icon: ChartBar, required: REQUIRES.TRACKING },
  { to: "/mis-descargas", tKey: "downloads", icon: Sparkle, required: 2, premium: true },
  { to: "/biblioteca", tKey: "library", icon: Books, required: 0 },
  { to: "/evaluacion", tKey: "evaluation", icon: Pulse, required: 0 },
];

const ADMIN_EXTRA = [
  { to: "/whatsapp", tKey: "whatsapp", icon: ChatCircleDots, required: 99 },
];

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const firstName = user?.name?.split(" ")[0] || t("dashboard.default_athlete_name");
  const isAdmin = user?.role === "admin";
  const plan = user?.effective_plan;
  const modules = isAdmin ? [...CLIENT_MODULES, ...ADMIN_EXTRA] : CLIENT_MODULES;

  const wp = user?.welcome_pack;
  const showFirstTimeModal = !!wp?.eligible && wp?.seen === false;
  const [modalOpen, setModalOpen] = useState(false);
  const [bannerHidden, setBannerHidden] = useState(false);

  useEffect(() => {
    if (showFirstTimeModal) setModalOpen(true);
  }, [showFirstTimeModal]);

  const dismissBanner = async () => {
    setBannerHidden(true);
    try {
      await api.post("/welcome-pack/dismiss");
      await refreshUser();
    } catch (e) {
      console.error("[Dashboard] dismiss banner:", e);
    }
  };

  const closeModal = async () => {
    setModalOpen(false);
    await refreshUser();
  };

  return (
    <Layout>
      <section className="border-b border-as-border bg-as-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="as-label text-as-green mb-3">{t("dashboard.eyebrow")}</p>
          <h1 className="as-heading text-5xl sm:text-6xl text-white" data-testid="dashboard-greeting">
            {t("dashboard.greeting", { name: firstName.toUpperCase() })
              .split(new RegExp(`(${firstName.toUpperCase()}\\.)`))
              .map((chunk, i) =>
                chunk === `${firstName.toUpperCase()}.` ? (
                  <span key={i} className="text-as-green">{chunk}</span>
                ) : (
                  chunk
                )
              )}
          </h1>
          <p className="text-as-gray mt-4 max-w-xl">
            {t("dashboard.subtitle")}
          </p>
          <div className="mt-6 inline-flex items-center gap-3 border border-as-border px-4 py-2" data-testid="dashboard-plan-badge">
            <span className="as-label">{t("dashboard.current_plan_label")}</span>
            <span className="as-label text-as-green">
              {isAdmin
                ? t("dashboard.plan_admin_full")
                : plan?.active
                  ? PLAN_NAMES[plan.plan]
                  : t("dashboard.no_active_plan")}
            </span>
          </div>
        </div>
      </section>

      <section className="bg-as-surface py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {wp?.eligible && !bannerHidden && (
            <WelcomeBanner
              plan={wp.plan_at_grant || plan?.plan}
              userName={user?.name}
              onDismiss={dismissBanner}
            />
          )}

          <p className="as-label text-as-green mb-6">{t("dashboard.modules_label")}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((m) => {
              const accessible = canAccess(user, m.required);
              const title = t(`dashboard.mod_${m.tKey}_title`);
              const desc = t(`dashboard.mod_${m.tKey}_desc`);
              return (
                <Link
                  key={m.to}
                  to={accessible ? m.to : "/planes"}
                  className={`as-card group block transition-all ${
                    accessible
                      ? "hover:border-as-green hover:-translate-y-1"
                      : "opacity-60 hover:border-as-border"
                  }`}
                  data-testid={`module-${m.tKey}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <m.icon size={36} weight="duotone" color="#39FF14" />
                    {!accessible && <LockKey size={22} weight="fill" color="#A0A0A0" />}
                  </div>
                  <h3 className="as-heading text-2xl text-white mb-2">{title}</h3>
                  <p className="text-sm text-as-gray mb-4">{desc}</p>
                  <span className="as-label text-as-green flex items-center gap-1">
                    {accessible ? t("dashboard.open") : t("dashboard.requires_plan", { plan: planNameForLevel(m.required) })}
                    <ArrowRight size={14} weight="bold" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <WelcomeModal open={modalOpen} onClose={closeModal} autoConfetti />
    </Layout>
  );
}
